import React, { useEffect, useState } from "react";
import useApi from "../../../hooks/useGaneshApi";
import { OmniSummary_T } from "../../../types/Brand";
import {
  getDateValue,
  renderDateCell,
  renderNumberCell,
  renderExternalLinkCell,
} from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import { useError } from "../../../contexts/ErrorContext";
import {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { Link } from "react-router-dom";

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "omni_key", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 50, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

const columns_def: GridColDef[] = [
  {
    field: "omni_key",
    headerName: "Omni Key",
    width: 200,
    renderCell: (params: GridRenderCellParams<String>) => (
      <Link to={`/data-ops/omni/${params.value}`}>{params.value}</Link>
    ),
  },
  {
    field: "data_source_id",
    headerName: "Data Source ID",
    type: "string",
    width: 200,
  },
  { field: "omni_external_id", headerName: "Omni External ID", width: 200 },
  { field: "brand_id", headerName: "Brand ID", type: "string", width: 150 },
  { field: "brand_name", headerName: "Brand Name", width: 200 },
  { field: "chain_id", headerName: "Chain ID", type: "string", width: 150 },
  {
    field: "data_source_created_at",
    headerName: "Data Source Created At",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "frozen_at",
    headerName: "Frozen At",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "omni_place_count",
    headerName: "Omni Place Count",
    type: "number",
    width: 200,
    renderCell: renderNumberCell,
  },
  {
    field: "open_omni_place_count",
    headerName: "Open Omni Place Count",
    type: "number",
    width: 200,
    renderCell: renderNumberCell,
  },
  {
    field: "closed_omni_place_count",
    headerName: "Closed Omni Place Count",
    type: "number",
    width: 200,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_duplicate_count",
    headerName: "Duplicate Count",
    type: "number",
    width: 200,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_non_duplicate_count",
    headerName: "Non-Duplicate Count",
    type: "number",
    width: 200,
    renderCell: renderNumberCell,
  },
  {
    field: "building_polygon_count",
    headerName: "Building Polygon Count",
    type: "number",
    width: 200,
    renderCell: renderNumberCell,
  },
  {
    field: "top_listed_classification",
    headerName: "Top Listed Classification",
    width: 300,
  },
  {
    field: "data_source_browseable_url",
    headerName: "Data Source URL",
    width: 300,
    renderCell: (params: GridRenderCellParams) =>
      renderExternalLinkCell(params),
  },
  {
    field: "omni_first_observation",
    headerName: "First Observation",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "omni_last_observation",
    headerName: "Last Observation",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "query_last_executed",
    headerName: "Data Last Updated",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  { field: "countries", headerName: "Countries", width: 1000 },
];

const AllOmnisSummaryTable: React.FC<any> = () => {
  const [omnisSummaryData, setOmnisSummaryData] = useState<OmniSummary_T[]>([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    const fetchOmnisSummary = async () => {
      try {
        const response = await apiRequest<OmniSummary_T[]>(
          `/brand_reports/omnis_summary`,
          { method: "GET" }
        );
        if (response.data) {
          setOmnisSummaryData(response.data);
        }
      } catch (error) {
        console.error(error);
        showError(`Failed to fetch omnis summaries.`);
      } finally {
        setLoading(false);
      }
    };

    fetchOmnisSummary();
  }, []);

  return (
    <GaneshDataGrid
      loading={loading}
      data={omnisSummaryData}
      columns={columns_def}
      gridId="allOmnisSummaryGrid"
      defaultGridState={defaultGridState}
      getRowHeight={() => "auto"}
    />
  );
};

export default AllOmnisSummaryTable;
